import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Empty, Row } from 'antd';
import PropTypes from 'prop-types';
import { Select } from 'formik-antd';
import { useIntl } from 'react-intl';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import { EMPTY_VALUE_PLACEHOLDER } from '../../../../const/system';
import { TYPE_DICTIONARY } from '../../const/templates';
import { useGetLanguagesQuery } from '../../../Translations/api/languagesApiSlice';
import getTemplateVisualizationsOptions from '../../../../lib/getTemplateVisualizationsOptions';
import getLanguagesOptions from '../../../../lib/getLanguagesOptions';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import Spinner from '../../../../components/Spinner';
import useFetchDictionariesByIds from '../../../Content/hooks/useFetchDictionariesByIds';



const TemplateVisualization = ({
  isLoading = false,
  currentValues = {},
  visualizations = [],
  dynamicData = [],
}) => {
  const intl = useIntl();
  const containerRef = useRef(null);
  const [ visualizationId, setVisualizationId ] = useState(null);
  const [ languageId, setLanguageId ] = useState(null);
  const [ reRender, setReRender ] = useState(false);
  const [ langList, setLangList ] = useState([]);
  const [ dictionaries, fetchDictionaries ] = useFetchDictionariesByIds();


  const currentDynamicValues = currentValues?.template_id ? currentValues.data : {};
  const visualizationsData = currentValues?.template_id ? visualizations : [];
  const dictionaryFields = dynamicData?.filter((item) => item.type === TYPE_DICTIONARY);
  const disableSwitcher = isEmpty(currentDynamicValues) && !isLoading;

  const valuesFromData = dictionaryFields?.map((field) => {
    const matchedKey = Object.keys(currentDynamicValues).find(
      (key) => currentDynamicValues[key]?.template_field_id === field.id,
    );

    if (matchedKey) {
      return {
        value: currentDynamicValues[matchedKey].value,
      };
    }

    return null;
  })
    .filter(Boolean);

  const dictionaryIds = useMemo(() => {
    return valuesFromData
      .map((item) => item?.value)
      .filter((value) => value != null);
  }, [ valuesFromData ]);

  // JSON.stringify(dictionaryIds) предотвращает ненужные повторные рендеры
  useEffect(() => {
    if (dictionaryIds?.length) {
      fetchDictionaries(dictionaryIds);
    } else {
      fetchDictionaries([]);
    }
  }, [ JSON.stringify(dictionaryIds) ]);

  const shouldFetchLanguages = dictionaryIds?.length > 0;

  const {
    data: { data: languages = [] } = { data: [] },
  } = useGetLanguagesQuery({ queryParams: 'limit=0' }, { skip: !shouldFetchLanguages });

  useEffect(() => {
    if (Array.isArray(dictionaries) && dictionaries.length && languages?.length) {
      const allLanguageIds = dictionaries
        .flatMap((dict) => dict?.structure?.data?.language_ids || []);

      const uniqueLanguageIds = [ ...new Set(allLanguageIds) ];

      const list = uniqueLanguageIds
        .map((langId) => languages.find((lang) => lang.id === langId))
        .filter(Boolean);

      setLangList(list);
    }
  }, [ dictionaries, languages ]);


  const getVisualization = () => {
    if (visualizationId) {
      return visualizationsData.find((item) => item.id === visualizationId);
    }
    return visualizationsData.find((item) => item.is_default === true) || null;
  };

  const triggerRender = () => {
    if (reRender) {
      setReRender((prev) => !prev);
    }
  };

  const replaceDictionariesPlaceholdersInHtml = (html, fields, translations) => {
    fields.forEach((field) => {
      const translation = translations.find(
        (item) => item.structure_field_id === field.id &&
          languageId === item.language_id,
      );

      if (translation) {
        const placeholder = new RegExp(`{{\\s*[^{}]*?dict-${translation.label}\\s*}}`, 'g');
        const value = translation.text ?? EMPTY_VALUE_PLACEHOLDER;

        html = html.replace(placeholder, value);
      }
    });

    return html;
  };

  const renderTemplate = (html) => {
    if (!html) return '';

    if (!languageId && langList[0]) {
      setLanguageId(langList[0].id);
    }

    dictionaries.forEach((dictionary) => {
      const fields = dictionary?.structure?.data?.fields?.data || [];
      const translations = dictionary?.translations?.data || [];

      html = replaceDictionariesPlaceholdersInHtml(html, fields, translations);
    });

    html = Object.entries(currentDynamicValues).reduce((result, [ key, { value } ]) => {
      setReRender(false);
      let replacement = '';

      if (Array.isArray(value) && value.length > 0) {
        const file = value[0];

        replacement = file?.thumbUrl ?? '';
        triggerRender();
      } else if (!isNil(value)) {
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;

        if (isString(value) && isoDateRegex.test(value)) {
          const date = new Date(value);

          if (!isNaN(date)) {
            replacement = new Intl.DateTimeFormat('en-GB', {
              day: 'numeric',
              month: 'short',
            }).format(date);
          } else {
            replacement = value;
          }
        } else {
          replacement = value;
        }
      }
      return result.replaceAll(`{{${key}}}`, replacement);
    }, html);

    return html;
  };

  const waitForThumbUrl = (file) => {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (file.thumbUrl) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  };

  const isFileArray = (value) => Array.isArray(value) && value.length > 0;

  const needsThumbUrlLoading = (file) => isObject(file) && 'type' in file && 'originFileObj' in file && !file.thumbUrl;

  const processValue = async (value) => {
    if (isFileArray(value)) {
      const file = value[0];

      if (needsThumbUrlLoading(file)) {
        await waitForThumbUrl(file);
      }
    }
  };

  const ensureUploadedFileIsLoaded = async (currentDynamicValues) => {
    const promises = Object.entries(currentDynamicValues)
      .map(([ _, { value } ]) => processValue(value));

    await Promise.all(promises);
  };

  requestAnimationFrame(async () => {
    const visualization = getVisualization();

    await ensureUploadedFileIsLoaded(currentDynamicValues);

    const rendered = renderTemplate(visualization?.html);

    if (containerRef.current) {
      containerRef.current.innerHTML = rendered;
    }
  });

  return (
    <Spinner spinning={isLoading}>
      <Row className="hp-text-center hp-mt-30">
        <Col span={24}>
          <Select
            name="visualization"
            value={getVisualization()?.title ?? null}
            disabled={disableSwitcher}
            className="template-visualization-select"
            placeholder={intl.formatMessage({ id: 'templates-visualizations-data-choose' })}
            options={getTemplateVisualizationsOptions(visualizationsData)}
            filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
            onChange={(value) => setVisualizationId(value)}
          />
        </Col>
        <Col span={24}>
          <Select
            name="language"
            value={languageId}
            disabled={disableSwitcher}
            className="template-visualization-select hp-mt-32"
            placeholder={intl.formatMessage({ id: 'form-languages-name-placeholder' })}
            options={getLanguagesOptions(langList)}
            filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
            onChange={(value) => setLanguageId(value)}
          />
        </Col>

        <div className="hp-mt-32 hp-mb-32 template-visualization-block ant-card">
          {getVisualization() && !isLoading ?
            <div ref={containerRef} /> :
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        </div>
      </Row>
    </Spinner>
  );
};

TemplateVisualization.propTypes = {
  isLoading: PropTypes.bool,
  visualizations: PropTypes.array,
  currentValues: PropTypes.object,
  dynamicData: PropTypes.array,
};

export default TemplateVisualization;
